// Pure display-name helpers for staff identity (BL-1104 / BUG-1154).
//
// Two rules that MUST agree on the server and the client, so they live here as
// IsomorphicServices (ADR 010 — pure, no I/O, safe in `useMemo`) rather than
// being re-derived at each call site:
//
//   1. How a captured first + last name composes into the single display string
//      `staff_users.name` requires. The server writes it at invite-accept; the
//      Members list renders the same composition for a pending invite that has
//      no staff row yet. Two implementations would drift the moment one of them
//      learns to trim, or to handle a missing last name.
//
//   2. Whether a stored name is the email placeholder BUG-1154 produced. Both
//      the enrollment pre-fill (client) and any future server-side cleanup need
//      the identical predicate — a looser one on either side would either strip
//      a real name or let the placeholder through again.

/** The captured name parts — matches `user_invites.first_name` / `last_name`. */
export interface StaffNameParts {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Unicode bidirectional embedding, override and isolate controls (U+202A to U+202E,
 * U+2066 to U+2069). One of these in a name leaks past it and reverses the text that
 * follows on a chip, an Inbox row, a pop-up or an email subject (BUG-2259). Letters of
 * a right-to-left script are not controls and are untouched.
 */
const BIDI_CONTROLS = new RegExp(
  `[${String.fromCharCode(0x202a)}-${String.fromCharCode(0x202e)}${String.fromCharCode(0x2066)}-${String.fromCharCode(0x2069)}]`,
  'g',
);

/** A name part with its bidirectional controls removed. */
function stripBidiControls(value: string): string {
  return value.replace(BIDI_CONTROLS, '');
}

/**
 * Compose the single display name from captured first + last name parts.
 *
 * Blank-tolerant on purpose: invites created before BL-1104 carry NULL parts,
 * and a caller may hold only one half. Whitespace-only input counts as absent,
 * so a stray space can never masquerade as a name. Bidirectional controls are
 * dropped (BUG-2259), so a part made only of them counts as absent too, and a
 * name already stored with one reads clean without a data fix.
 *
 * @param parts - The captured first and/or last name (either may be absent).
 * @returns The trimmed parts joined by a single space, or `null` when neither
 *   part carries any non-whitespace content.
 */
export function composeStaffDisplayName(parts: StaffNameParts): string | null {
  const first = stripBidiControls(parts.firstName ?? '').trim();
  const last = stripBidiControls(parts.lastName ?? '').trim();
  const composed = [first, last].filter(Boolean).join(' ');
  return composed.length > 0 ? composed : null;
}

/**
 * Derive first + last name parts from a single composed display name.
 *
 * Splits on the FIRST space: the first token is the given name, everything after
 * it is the family name. That keeps surname particles together — 'Mary Anne van
 * der Berg' yields `van der Berg`, not `Berg` — which a last-space split would
 * silently discard.
 *
 * **This MUST stay identical to the SQL backfill** in
 * `db/migrations/20260811183000_bl1170_staff_users_first_last_name.sql`
 * (BL-1170). A backfilled row and a freshly-derived one disagreeing is a silent
 * data inconsistency, so the round-trip through {@link composeStaffDisplayName}
 * is asserted in both the unit suite and the migration's verification query.
 *
 * @param name - A composed display name (`staff_users.name`), possibly blank.
 * @returns The derived parts; both `null` when the input carries no content.
 */
export function splitStaffDisplayName(name: string | null | undefined): StaffNameParts {
  const tokens = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { firstName: null, lastName: null };
  const [first, ...rest] = tokens;
  return { firstName: first, lastName: rest.length > 0 ? rest.join(' ') : null };
}

/**
 * Whether a surname token should be skipped when picking the family initial.
 *
 * Two skippable shapes, both of which sit BEFORE the actual family name:
 *
 * 1. **Particles** — `van`, `der`, `de`, `von`, `bin`, … Detected by case, not a
 *    word list: the convention across languages is that particles are lowercase
 *    while the name itself is capitalized, so this stays correct for particles no
 *    list would have (and doesn't misfire on a legitimately capitalized 'Van').
 * 2. **Middle initials** — `B` / `B.`. These land in `last_name` whenever a
 *    legacy composed name like 'Grace B. Hopper' is split on the first space, so
 *    without this the avatar would read 'GB' instead of 'GH'.
 */
function isSkippableSurnameToken(token: string): boolean {
  if (/^[A-Za-z]\.?$/.test(token)) return true;
  return token === token.toLowerCase();
}

/**
 * Avatar initials for a staff member — first initial of the given name plus the
 * first initial of the family name.
 *
 * Accepts either the captured parts (preferred — the whole point of BL-1170) or
 * a composed display name for surfaces that only receive `staff_users.name`
 * (sales-rep cells, activity log). Passing a string routes through
 * {@link splitStaffDisplayName} so no call site re-implements the split.
 *
 * Particles and middle initials are skipped, so 'Mary Anne van der Berg'
 * initializes as `MB` rather than `MV` (BL-1170 AC 4) and a backfilled
 * 'Grace B. Hopper' (parts: 'Grace' / 'B. Hopper') gives `GH`, not `GB`. When
 * every surname token is skippable the first one is used, so an initial is never
 * dropped.
 *
 * @param input - The captured name parts, or a composed display name.
 * @returns One or two uppercase initials, or `'U'` when no part carries content.
 */
export function staffInitials(input: StaffNameParts | string | null | undefined): string {
  const parts = typeof input === 'string' || input == null ? splitStaffDisplayName(input) : input;

  // The same bidi strip as the display name (BUG-2259): an override first character is not an initial.
  const first = stripBidiControls(parts.firstName ?? '').trim();
  const lastTokens = stripBidiControls(parts.lastName ?? '').trim().split(/\s+/).filter(Boolean);
  // Prefer the first token that is the actual family name (skipping particles
  // and middle initials); fall back to the first token so an all-lowercase or
  // initial-only surname still contributes an initial rather than none.
  const last = lastTokens.find((t) => !isSkippableSurnameToken(t)) ?? lastTokens[0] ?? '';

  const initials = [first, last]
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return initials.length > 0 ? initials : 'U';
}

/**
 * Whether a stored display name is really the invitee's email address — the
 * BUG-1154 placeholder that Better Auth's required `name` field forced when the
 * invite carried no real name.
 *
 * Exact (case-insensitive, trimmed) equality, never a shape match: a name that
 * merely *looks* like an email but differs from this row's own address may be
 * something the person deliberately entered, and suppressing it would be the
 * mirror-image bug.
 *
 * @param name - The stored display name (`staff_users.name`).
 * @param email - The same row's email address.
 * @returns `true` only when both are present and equal ignoring case + surrounding
 *   whitespace — i.e. the name carries no information the email doesn't.
 */
export function isEmailPlaceholderName(
  name: string | null | undefined,
  email: string | null | undefined,
): boolean {
  const n = name?.trim().toLowerCase() ?? '';
  const e = email?.trim().toLowerCase() ?? '';
  if (!n || !e) return false;
  return n === e;
}

/**
 * What a staff member with no name on file renders as when we have no email
 * either — the single empty-state string for staff identity.
 *
 * Deliberately NOT the word "Unknown" (BUG-1650 AC 1): four surfaces each
 * invented their own answer for a nameless staff row — the full email, the
 * email local-part, the literal `'Unknown'`, and (for a `name` of `''`) a blank
 * line — so one operator saw four different names for themselves. This is the
 * only literal any staff surface may render, and it is reached only when the row
 * carries neither a name part nor an email (`staff_users.email` is nullable).
 *
 * The staff twin of ADR 058's customer `NO_NAME_ON_FILE`, deliberately a
 * SEPARATE constant with SEPARATE COPY. Reusing the customer sentinel's exact
 * wording trips `one-resolver-one-sentinel.test.ts` in
 * `packages/core/src/services/customers/__tests__/`, which enforces that
 * exactly one production file spells that literal — and the guard is right to
 * fire: ADR 058 says mirror the customer treatment's SHAPE, never reuse its
 * constant or its copy. Naming the audience also reads better where this
 * actually surfaces, which is an author byline.
 *
 * In practice it is near-unreachable: a row needs no name part AND no email to
 * get here, which only the NULL-email notification-roster seed produces today.
 */
export const NO_STAFF_NAME_ON_FILE = 'Unnamed staff member';

/** A staff row's display inputs: the captured parts plus its email address. */
export interface StaffDisplayNameInput extends StaffNameParts {
  /** `staff_users.email` — the fallback rung, used whole and never split. */
  email?: string | null;
}

/**
 * The ONE display name for a staff member, for every surface (BUG-1650).
 *
 * Two rungs and a sentinel:
 *
 *   1. The captured parts, composed — `first_name` / `last_name` are the truth
 *      (BL-1170). This is the only rung that yields a real name.
 *   2. The email address, **whole**. Never the local-part: `'dan'` reads as a
 *      real name, which is exactly how an email became a staff member's stored
 *      name twice (BUG-1154 via the invite pre-fill, BUG-1650 via My Profile's).
 *   3. {@link NO_STAFF_NAME_ON_FILE}, when the row has neither.
 *
 * There is deliberately NO rung for the login library's own name column
 * (`staff_users.name_from_better_auth_do_not_use`, formerly `staff_users.name`).
 * Better Auth's user model requires that field, so it writes one on every
 * create — `''` from email-OTP sign-in, the id-token's copy from OAuth — and a
 * reader that trusts it gets an answer that disagrees with the parts the
 * operator actually edits.
 *
 * @param input - The row's captured parts and email address.
 * @returns A non-empty display string, always.
 */
export function resolveStaffDisplayName(input: StaffDisplayNameInput): string {
  const composed = composeStaffDisplayName(input);
  if (composed) return composed;
  const email = input.email?.trim() ?? '';
  if (email) return email;
  return NO_STAFF_NAME_ON_FILE;
}
